import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { User } from 'api/models';
import { Action, EventType, ObjectType } from 'api/pushes';
import { AccountSocketData } from 'api/pushes/models';
import { ALL_MEMBERS_QUERY_KEY } from 'queries/members';
import { logger } from 'services';
import { useAccountWsUpdates } from '../use-account-ws-updates';
import { useWorker } from '../use-worker';

export const useAccountUserWsUpdates = (): void => {
  const worker = useWorker();
  const queryClient = useQueryClient();

  const handleUserAdd = useCallback(
    async (socketData: AccountSocketData<User>) => {
      worker.workerApi.addMember(socketData?.data).catch((err) => {
        logger.error(
          'Error occured while inserting new member in indexedDB in settings page',
          err,
        );
      });
      await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
    },
    [queryClient, worker.workerApi],
  );

  const handleUserUpdate = useCallback(
    async (socketData: AccountSocketData<User>) => {
      worker.workerApi.updateMember(socketData?.data).catch((err) => {
        logger.error(
          'Error occured while updating new member in indexedDB in settings page',
          err,
        );
      });
      await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
    },
    [queryClient, worker.workerApi],
  );

  const handleUserDelete = useCallback(
    async (socketData: AccountSocketData<User>) => {
      await worker.workerApi
        .deleteMember(socketData?.data?.userId)
        .catch((err) => {
          logger.error(
            'Error occured while deleting new member in indexedDB in settings page',
            err,
          );
        });
      await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
    },
    [queryClient, worker.workerApi],
  );

  useAccountWsUpdates(handleUserAdd, EventType(Action.Create, ObjectType.User));
  useAccountWsUpdates(
    handleUserUpdate,
    EventType(Action.Update, ObjectType.User),
  );
  useAccountWsUpdates(
    handleUserDelete,
    EventType(Action.Delete, ObjectType.User),
  );
};
